import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, User, Video, Phone, MapPin, 
  ChevronLeft, ChevronRight, Check, ArrowLeft,
  Loader2, AlertCircle, Lock, LogIn, Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Logo URL
const LOGO_URL = "https://i.ibb.co/Y781pDXX/logo-dark.png";

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // URL params
  const preselectedStaff = searchParams.get('staff');
  const clientIdParam = searchParams.get('client_id');
  const inviteToken = searchParams.get('invite');
  
  // Auth & Access State
  const [authLoading, setAuthLoading] = useState(true);
  const [currentClient, setCurrentClient] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [accessError, setAccessError] = useState(null);
  
  // Step State
  const [step, setStep] = useState(1); // 1: type, 2: staff, 3: date/time, 4: details, 5: confirm
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(preselectedStaff || '');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [locationType, setLocationType] = useState('zoom');
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [timezone, setTimezone] = useState('America/New_York');
  
  // ============ AUTH & ACCESS CHECKS ============
  
  useEffect(() => {
    checkAccessAndLoadData();
  }, [inviteToken]);
  
  const checkAccessAndLoadData = async () => {
    setAuthLoading(true);
    setAccessError(null);
    
    try {
      // Step 1: Check if user has a valid invite token
      if (inviteToken) {
        const inviteRes = await fetch(`${API}/api/booking/invite/${inviteToken}`);
        if (inviteRes.ok) {
          const data = await inviteRes.json();
          setInviteData(data.invite);
          // Pre-fill client info from invite
          setClientInfo(prev => ({
            ...prev,
            name: data.invite.lead_name || '',
            email: data.invite.lead_email || ''
          }));
          // Pre-select staff from invite
          if (data.invite.staff_username) {
            setSelectedStaff(data.invite.staff_username);
          }
        } else {
          const errData = await inviteRes.json();
          setAccessError({
            type: 'invalid_invite',
            message: errData.detail || 'This booking link is invalid or has expired.',
            showLogin: false
          });
          setAuthLoading(false);
          return;
        }
      }
      
      // Step 2: Check if user is logged in as a client
      const clientRes = await fetch(`${API}/api/client/me`, { credentials: 'include' });
      if (clientRes.ok) {
        const clientData = await clientRes.json();
        setCurrentClient(clientData);
        // Pre-fill client info if logged in
        if (!inviteToken) {
          setClientInfo(prev => ({
            ...prev,
            name: `${clientData.primary_contact_first_name || ''} ${clientData.primary_contact_last_name || ''}`.trim(),
            email: clientData.primary_contact_email || ''
          }));
        }
      }
      
      // Step 3: Load meeting types and staff
      await fetchInitialData();
      
    } catch (err) {
      console.error('Error checking access:', err);
      setAccessError({
        type: 'error',
        message: 'Failed to load booking page. Please try again.',
        showLogin: false
      });
    } finally {
      setAuthLoading(false);
    }
  };
  
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Build URL with invite token if present
      let typesUrl = `${API}/api/booking/meeting-types`;
      if (inviteToken) {
        typesUrl += `?invite=${inviteToken}`;
      }
      
      const [typesRes, staffRes] = await Promise.all([
        fetch(typesUrl, { credentials: 'include' }),
        fetch(`${API}/api/booking/staff`, { credentials: 'include' })
      ]);
      
      if (typesRes.ok) {
        const data = await typesRes.json();
        setMeetingTypes(data.meeting_types || []);
      }
      
      if (staffRes.ok) {
        const data = await staffRes.json();
        const availableStaff = (data.staff || []).filter(s => s.has_availability);
        setStaffList(availableStaff);
        
        if (preselectedStaff) {
          const staffExists = availableStaff.find(s => s.username === preselectedStaff);
          if (staffExists) {
            setSelectedStaff(preselectedStaff);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load booking data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle meeting type selection with security check
  const handleSelectMeetingType = (type) => {
    // Check if this meeting type requires client login
    if (type.requires_client_login && !currentClient && !inviteToken) {
      setAccessError({
        type: 'login_required',
        message: 'You need to be logged in as a client to book this meeting type.',
        showLogin: true,
        meetingName: type.name
      });
      return;
    }
    
    setSelectedType(type);
    
    // If invite has pre-selected staff, skip staff selection
    if (inviteData?.staff_username) {
      setStep(3);
    } else if (preselectedStaff) {
      setStep(3);
    } else {
      setStep(2);
    }
  };
  
  // Auto-select staff if client_id provided
  useEffect(() => {
    if (clientIdParam && !preselectedStaff) {
      fetchClientManager();
    }
  }, [clientIdParam]);
  
  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate && selectedStaff && selectedType) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedStaff, selectedType]);
  
  const fetchClientManager = async () => {
    try {
      const res = await fetch(`${API}/api/booking/client/${clientIdParam}/manager`);
      if (res.ok) {
        const data = await res.json();
        if (data.manager) {
          setSelectedStaff(data.manager.username);
          if (data.client_name) {
            setClientInfo(prev => ({ ...prev, name: data.client_name }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching client manager:', err);
    }
  };
  
  const fetchAvailableSlots = async () => {
    setSlotsLoading(true);
    setAvailableSlots([]);
    setSelectedSlot(null);
    
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const res = await fetch(
        `${API}/api/booking/availability?staff=${selectedStaff}&date=${dateStr}&meeting_type=${selectedType.type_id}`,
        { credentials: 'include' }
      );
      
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.slots || []);
        setTimezone(data.timezone || 'America/New_York');
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setSlotsLoading(false);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      const res = await fetch(`${API}/api/booking/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          meeting_type: selectedType.type_id,
          start_time: selectedSlot.start_time,
          client_id: currentClient?.client_id || clientIdParam || null,
          client_name: clientInfo.name,
          client_email: clientInfo.email,
          staff_username: selectedStaff,
          location_type: locationType,
          notes: clientInfo.notes,
          invite_token: inviteToken || null
        })
      });
      
      if (res.ok) {
        setStep(5); // Success step
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to book appointment');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };
  
  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return false;
    
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    if (date > maxDate) return false;
    
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return false;
    
    return true;
  };
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // ============ RENDER ACCESS ERROR ============
  
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Checking access...</p>
        </div>
      </div>
    );
  }
  
  if (accessError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <header className="bg-white border-b border-slate-200 py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link to="/">
              <img src={LOGO_URL} alt="BirdenMedia" className="h-8" />
            </Link>
            <span className="text-sm text-slate-500">Schedule a Meeting</span>
          </div>
        </header>
        
        <main className="max-w-lg mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center"
          >
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
              accessError.type === 'login_required' ? 'bg-blue-100' : 'bg-red-100'
            }`}>
              {accessError.type === 'login_required' ? (
                <LogIn className="w-8 h-8 text-blue-600" />
              ) : (
                <Lock className="w-8 h-8 text-red-500" />
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              {accessError.type === 'login_required' ? 'Login Required' : 'Access Denied'}
            </h2>
            
            <p className="text-slate-600 mb-6">
              {accessError.message}
            </p>
            
            {accessError.showLogin && (
              <div className="space-y-3">
                <Link
                  to="/client/login"
                  className="block w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
                >
                  Login to Continue
                </Link>
                <Link
                  to="/"
                  className="block w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
                >
                  Back to Home
                </Link>
              </div>
            )}
            
            {!accessError.showLogin && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">
                  If you believe this is an error, please contact your account manager.
                </p>
                <Link
                  to="/"
                  className="block w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
                >
                  Back to Home
                </Link>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    );
  }
  
  // ============ RENDER LOADING STATE ============
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading booking options...</p>
        </div>
      </div>
    );
  }
  
  // ============ MAIN RENDER ============
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="BirdenMedia" className="h-8" />
          </Link>
          <div className="flex items-center gap-4">
            {currentClient && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <Check className="w-4 h-4" />
                Logged in as {currentClient.business_name}
              </span>
            )}
            {inviteData && (
              <span className="text-sm text-blue-600 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Invited: {inviteData.lead_email}
              </span>
            )}
            <span className="text-sm text-slate-500">Schedule a Meeting</span>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        {step < 5 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step >= s 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-12 h-1 rounded transition-all ${
                    step > s ? 'bg-blue-600' : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </motion.div>
        )}
        
        <AnimatePresence mode="wait">
          {/* Step 1: Select Meeting Type */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Select Meeting Type</h2>
              <p className="text-slate-600 mb-6">Choose the type of meeting you'd like to schedule.</p>
              
              {/* Show invite-specific meeting if using invite */}
              {inviteData && (
                <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <strong>You've been invited to book a {inviteData.meeting_name}</strong> with {inviteData.staff_name || inviteData.staff_username}.
                  </p>
                </div>
              )}
              
              <div className="grid gap-4">
                {meetingTypes.map((type) => (
                  <button
                    key={type.type_id}
                    onClick={() => handleSelectMeetingType(type)}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                      selectedType?.type_id === type.type_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-3 h-12 rounded-full" 
                        style={{ backgroundColor: type.color }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{type.name}</h3>
                          {type.requires_client_login && !currentClient && !inviteToken && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Login Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{type.description}</p>
                      </div>
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{type.duration_min} min</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Login prompt if no client logged in and no invite */}
              {!currentClient && !inviteToken && (
                <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-sm text-slate-600 mb-3">
                    <strong>Existing client?</strong> Log in to access all meeting types.
                  </p>
                  <Link
                    to="/client/login"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    <LogIn className="w-4 h-4" />
                    Login to your account
                  </Link>
                </div>
              )}
            </motion.div>
          )}
          
          {/* Step 2: Select Staff */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
            >
              <button 
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Select Team Member</h2>
              <p className="text-slate-600 mb-6">Choose who you'd like to meet with.</p>
              
              <div className="grid gap-4">
                {staffList.map((staff) => (
                  <button
                    key={staff.username}
                    onClick={() => {
                      setSelectedStaff(staff.username);
                      setStep(3);
                    }}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                      selectedStaff === staff.username
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                        {staff.full_name?.charAt(0) || staff.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{staff.full_name || staff.username}</h3>
                        <p className="text-sm text-slate-500 capitalize">{staff.role?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
          
          {/* Step 3: Select Date & Time */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
            >
              <button 
                onClick={() => setStep(inviteData?.staff_username || preselectedStaff ? 1 : 2)}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Select Date & Time</h2>
              <p className="text-slate-600 mb-6">
                {selectedType?.name} with {staffList.find(s => s.username === selectedStaff)?.full_name || selectedStaff}
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="p-2 hover:bg-slate-100 rounded-lg"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h3 className="font-semibold">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="p-2 hover:bg-slate-100 rounded-lg"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-center text-sm mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="py-2 text-slate-500 font-medium">{day}</div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {getDaysInMonth(currentMonth).map((date, idx) => (
                      <button
                        key={idx}
                        disabled={!isDateSelectable(date)}
                        onClick={() => date && isDateSelectable(date) && setSelectedDate(date)}
                        className={`py-2 rounded-lg text-sm transition-all ${
                          !date 
                            ? 'invisible' 
                            : !isDateSelectable(date)
                            ? 'text-slate-300 cursor-not-allowed'
                            : selectedDate?.toDateString() === date.toDateString()
                            ? 'bg-blue-600 text-white font-semibold'
                            : 'hover:bg-blue-50 text-slate-700'
                        }`}
                      >
                        {date?.getDate()}
                      </button>
                    ))}
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-4 text-center">
                    Timezone: {timezone}
                  </p>
                </div>
                
                {/* Time Slots */}
                <div>
                  {selectedDate ? (
                    <>
                      <h3 className="font-semibold text-slate-800 mb-4">
                        {formatDate(selectedDate)}
                      </h3>
                      
                      {slotsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                          <p>No available times on this day</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {availableSlots.map((slot, idx) => (
                            <button
                              key={idx}
                              onClick={() => setSelectedSlot(slot)}
                              className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                selectedSlot?.start_time === slot.start_time
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-slate-100 hover:bg-blue-100 text-slate-700'
                              }`}
                            >
                              {slot.formatted_time.split(' - ')[0]}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>Select a date to see available times</p>
                    </div>
                  )}
                  
                  {selectedSlot && (
                    <button
                      onClick={() => setStep(4)}
                      className="w-full mt-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
                    >
                      Continue
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Step 4: Enter Details */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
            >
              <button 
                onClick={() => setStep(3)}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Enter Your Details</h2>
              <p className="text-slate-600 mb-6">Please provide your information to complete the booking.</p>
              
              {/* Summary */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-8 rounded-full" style={{ backgroundColor: selectedType?.color }} />
                  <div>
                    <p className="font-semibold text-slate-800">{selectedType?.name}</p>
                    <p className="text-sm text-slate-500">{selectedType?.duration_min} minutes</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                  <Calendar className="w-4 h-4" />
                  <span>{selectedDate && formatDate(selectedDate)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4" />
                  <span>{selectedSlot?.formatted_time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <User className="w-4 h-4" />
                  <span>{staffList.find(s => s.username === selectedStaff)?.full_name}</span>
                </div>
              </div>
              
              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Your Name *</label>
                  <input
                    type="text"
                    value={clientInfo.name}
                    onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Smith"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="you@company.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Meeting Location</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { id: 'zoom', icon: Video, label: 'Zoom' },
                      { id: 'google_meet', icon: Video, label: 'Google Meet' },
                      { id: 'phone', icon: Phone, label: 'Phone' },
                      { id: 'in_person', icon: MapPin, label: 'In Person' },
                    ].map(loc => (
                      <button
                        key={loc.id}
                        type="button"
                        onClick={() => setLocationType(loc.id)}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          locationType === loc.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        <loc.icon className={`w-5 h-5 mx-auto mb-1 ${
                          locationType === loc.id ? 'text-blue-600' : 'text-slate-400'
                        }`} />
                        <span className="text-xs font-medium">{loc.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Notes <span className="text-slate-400">(optional)</span>
                  </label>
                  <textarea
                    value={clientInfo.notes}
                    onChange={(e) => setClientInfo({ ...clientInfo, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Anything you'd like us to know before the meeting..."
                  />
                </div>
                
                <button
                  onClick={handleSubmit}
                  disabled={!clientInfo.name || !clientInfo.email || submitting}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Booking...
                    </>
                  ) : (
                    'Confirm Booking'
                  )}
                </button>
              </div>
            </motion.div>
          )}
          
          {/* Step 5: Success */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Booking Confirmed!</h2>
              <p className="text-slate-600 mb-6">
                Your {selectedType?.name?.toLowerCase()} has been scheduled.
              </p>
              
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left max-w-sm mx-auto">
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span>{selectedDate && formatDate(selectedDate)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <Clock className="w-4 h-4" />
                  <span>{selectedSlot?.formatted_time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <User className="w-4 h-4" />
                  <span>{staffList.find(s => s.username === selectedStaff)?.full_name}</span>
                </div>
              </div>
              
              <p className="text-sm text-slate-500 mb-6">
                A confirmation email will be sent to {clientInfo.email}
              </p>
              
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
              >
                Back to Home
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
